/**
 * Extract a thumbnail from a video or image file using canvas.
 * For videos: seeks to 0.5s and captures a frame.
 * For images: draws directly to canvas and resizes.
 * Returns a JPEG File object (max 300x300).
 */

const THUMB_SIZE = 300

function generateVideoThumbnail(file: File): Promise<File | null> {
  return new Promise(resolve => {
    let settled = false
    const video = document.createElement("video")
    video.preload = "auto"
    video.muted = true
    video.playsInline = true

    const url = URL.createObjectURL(file)
    video.src = url

    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true
        URL.revokeObjectURL(url)
        resolve(null)
      }
    }, 8000)

    const cleanup = () => {
      clearTimeout(timeoutId)
      URL.revokeObjectURL(url)
    }

    video.addEventListener("error", () => {
      if (settled) return
      settled = true
      cleanup()
      resolve(null)
    })

    video.addEventListener("loadeddata", () => {
      // Guard against NaN duration (some codecs don't report it immediately)
      const seekTo = isFinite(video.duration)
        ? Math.min(0.5, video.duration / 2)
        : 0
      video.currentTime = seekTo
    })

    video.addEventListener("seeked", () => {
      if (settled) return
      settled = true
      try {
        const canvas = document.createElement("canvas")
        const scale = Math.min(
          THUMB_SIZE / video.videoWidth,
          THUMB_SIZE / video.videoHeight,
          1,
        )
        canvas.width = Math.round(video.videoWidth * scale)
        canvas.height = Math.round(video.videoHeight * scale)

        const ctx = canvas.getContext("2d")
        if (!ctx) {
          cleanup()
          resolve(null)
          return
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          blob => {
            cleanup()
            if (!blob) {
              resolve(null)
              return
            }
            resolve(new File([blob], "thumbnail.jpg", { type: "image/jpeg" }))
          },
          "image/jpeg",
          0.85,
        )
      } catch {
        cleanup()
        resolve(null)
      }
    })
  })
}

function generateImageThumbnail(file: File): Promise<File | null> {
  return new Promise(resolve => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.src = url

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas")
        const scale = Math.min(
          THUMB_SIZE / img.width,
          THUMB_SIZE / img.height,
          1,
        )
        canvas.width = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)

        const ctx = canvas.getContext("2d")
        if (!ctx) {
          URL.revokeObjectURL(url)
          resolve(null)
          return
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          blob => {
            URL.revokeObjectURL(url)
            if (!blob) {
              resolve(null)
              return
            }
            resolve(new File([blob], "thumbnail.jpg", { type: "image/jpeg" }))
          },
          "image/jpeg",
          0.85,
        )
      } catch {
        URL.revokeObjectURL(url)
        resolve(null)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
  })
}

export async function generateThumbnail(file: File): Promise<File | null> {
  try {
    if (file.type.startsWith("video/")) {
      return await generateVideoThumbnail(file)
    }
    if (file.type.startsWith("image/")) {
      return await generateImageThumbnail(file)
    }
    return null
  } catch {
    return null
  }
}

/** Longest edge of a frame captured from a remote video. 1080 matches
 *  Meta's thumbnail recommendation (same cap as the AdForge thumbnail
 *  picker modal). */
const MAX_CAPTURE_EDGE = 1080

/**
 * Capture a single frame from a REMOTE video URL as a JPEG File — for
 * assets that already live in the library (a client-side `File` never
 * existed, e.g. Veo output uploaded server-side). Mirrors the mechanics
 * proven in VideoThumbnailPickerModal: `crossOrigin="anonymous"` so the
 * canvas isn't tainted (the asset bucket serves CORS headers), seek, draw,
 * encode.
 *
 * Resolves to null on ANY failure — CORS-blocked host, decode error,
 * timeout — a missing thumbnail must never fail the caller's flow.
 */
export function captureVideoFrameFromUrl(
  url: string,
  atSeconds = 0.5,
): Promise<File | null> {
  return new Promise(resolve => {
    let settled = false
    const video = document.createElement("video")
    // "metadata" is enough to seek + draw one frame; "auto" would pull the
    // whole remote file just for a thumbnail.
    video.preload = "metadata"
    video.muted = true
    video.playsInline = true
    // Required so the canvas capture isn't tainted by the cross-origin src.
    video.crossOrigin = "anonymous"

    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true
        resolve(null)
      }
    }, 15000)

    const finish = (file: File | null) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      resolve(file)
    }

    video.addEventListener("error", () => finish(null))

    video.addEventListener("loadeddata", () => {
      const seekTo = isFinite(video.duration)
        ? Math.min(atSeconds, video.duration / 2)
        : 0
      video.currentTime = seekTo
    })

    video.addEventListener("seeked", () => {
      try {
        const scale = Math.min(
          MAX_CAPTURE_EDGE / Math.max(video.videoWidth, video.videoHeight),
          1,
        )
        const canvas = document.createElement("canvas")
        canvas.width = Math.round(video.videoWidth * scale)
        canvas.height = Math.round(video.videoHeight * scale)
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          finish(null)
          return
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          blob =>
            finish(
              blob
                ? new File([blob], "thumbnail.jpg", { type: "image/jpeg" })
                : null,
            ),
          "image/jpeg",
          0.85,
        )
      } catch {
        // SecurityError (tainted canvas) or anything else — best-effort only.
        finish(null)
      }
    })

    video.src = url
  })
}

/**
 * Read a File back out as a data URL — the shape POST /assets/:id/thumbnail
 * expects for `imageBase64`.
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"))
    reader.readAsDataURL(file)
  })
}
